import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2, AlertTriangle, Ban } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// ClosePlanDialog — final close of a treatment plan. Three outcomes,
// picked from the plan's real state so the button never lies:
//
//   1. abandon  — plan still has unfinished (unrendered) work. Closing
//                 marks it Abandoned: finished visits keep their
//                 production credit, the remaining planned work is
//                 dropped and never billed. NOT AR — that work won't be
//                 rendered, so there's nothing to collect on it.
//                 Calls public.mark_plan_abandoned(plan_id, reason).
//   2. ar       — all work done, balance > 0. Closes as Completed and
//                 routes the balance to AR. Requires a reason.
//   3. complete — all work done, paid in full. Closes as Completed.
//
// Both (2) and (3) call public.close_plan(plan_id, ar_reason).
// =====================================================================

const AR_REASONS = [
  "Patient promised — pay next visit",
  "HMO claim pending",
  "Post-dated check",
  "Partial payment plan agreed",
  "Other",
];

const ABANDON_REASONS = [
  "Patient discontinued treatment",
  "Patient transferred to another clinic",
  "Remaining work no longer needed",
  "Other",
];

// Mirrors the server guard in dos_v2.close_plan: an item is "unfinished"
// unless it's been dropped (Cancelled / Declined) or finished (Completed).
function countUnfinishedItems(plan) {
  const items = Array.isArray(plan?.items) ? plan.items : [];
  return items.filter((it) => {
    const s = String(it?.status || "");
    return s !== "Cancelled" && s !== "Declined" && s !== "Completed";
  }).length;
}

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  return `₱${Math.round(centavos / 100).toLocaleString()}`;
}

export default function ClosePlanDialog({ open, onOpenChange, plan, balanceCentavos = 0, onClosed }) {
  const unfinishedCount = countUnfinishedItems(plan);
  const requiresAR = (Number(balanceCentavos) || 0) > 0;
  const mode = unfinishedCount > 0 ? "abandon" : (requiresAR ? "ar" : "complete");
  const presets = mode === "abandon" ? ABANDON_REASONS : AR_REASONS;
  const requiresReason = mode === "abandon" || mode === "ar";

  const [reasonChoice, setReasonChoice] = useState(presets[0]);
  const [reasonOther,  setReasonOther]  = useState("");
  const [submitting,   setSubmitting]   = useState(false);

  useEffect(() => {
    if (!open) return;
    setReasonChoice(mode === "abandon" ? ABANDON_REASONS[0] : AR_REASONS[0]);
    setReasonOther("");
    setSubmitting(false);
  }, [open, mode]);

  if (!open || !plan) return null;

  const reasonText = reasonChoice === "Other" ? reasonOther.trim() : reasonChoice;
  const canSubmit  = !submitting && (!requiresReason || (reasonText && reasonText.length > 0));

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);

    if (mode === "abandon") {
      const { data, error } = await supabase.rpc("mark_plan_abandoned", {
        p_plan_id: plan.id,
        p_reason:  reasonText,
      });
      setSubmitting(false);
      if (error) {
        toast.error(error.message ?? "Could not close plan.");
        return;
      }
      toast.success("Plan closed — marked Abandoned. Finished work kept its credit.");
      onClosed?.(data);
      onOpenChange?.(false);
      return;
    }

    const { data, error } = await supabase.rpc("close_plan", {
      p_plan_id:   plan.id,
      p_ar_reason: mode === "ar" ? reasonText : null,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "Could not close plan.");
      return;
    }
    if (data?.noop) {
      toast.message("Plan already closed.");
    } else if (data?.requires_ar) {
      toast.success(`Plan closed clinically — ${formatPHP(balanceCentavos)} routed to AR.`);
    } else {
      toast.success("Plan closed — paid in full.");
    }
    onClosed?.(data);
    onOpenChange?.(false);
  }

  const headerIcon = mode === "abandon" ? <Ban className="w-4 h-4 text-rose-600" />
    : mode === "ar" ? <AlertTriangle className="w-4 h-4 text-amber-600" />
    : <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
  const headerTitle = mode === "abandon" ? "Close plan with unfinished work"
    : mode === "ar" ? "Close Plan with Outstanding Balance"
    : "Close Plan";
  const submitLabel = mode === "abandon" ? "Close & mark Abandoned"
    : mode === "ar" ? "Close & Route to AR"
    : "Close Plan";
  const submitTone = mode === "abandon" ? "bg-rose-600 hover:bg-rose-700"
    : mode === "ar" ? "bg-amber-600 hover:bg-amber-700"
    : "bg-emerald-600 hover:bg-emerald-700";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
         role="dialog" aria-modal="true" data-testid="close-plan-dialog" data-mode={mode}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
              {headerIcon}
              {headerTitle}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Outstanding <span className="font-semibold text-slate-900">{formatPHP(balanceCentavos)}</span>
            </p>
          </div>
          <button type="button" onClick={() => onOpenChange?.(false)}
                  className="text-slate-400 hover:text-slate-700"
                  aria-label="Close" data-testid="close-plan-close-button">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {mode === "abandon" ? (
            <>
              <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-3 text-sm text-rose-800" data-testid="close-plan-abandon-note">
                This plan still has <span className="font-semibold">{unfinishedCount} unfinished item{unfinishedCount === 1 ? "" : "s"}</span>.
                Closing now marks the plan <span className="font-semibold">Abandoned</span> — finished visits keep their
                production credit, and the remaining planned work is dropped (never billed).
                It won&apos;t go to AR, because that work won&apos;t be rendered.
              </div>
              <div>
                <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                  Why is it being abandoned?
                </p>
                <div className="space-y-1.5">
                  {ABANDON_REASONS.map((r) => (
                    <label key={r}
                           data-testid={`close-plan-reason-${r.replace(/\s+/g,'-').toLowerCase()}`}
                           className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${
                             reasonChoice === r
                               ? "border-rose-500 bg-rose-50"
                               : "border-slate-200 bg-white hover:border-slate-400"
                           }`}>
                      <input type="radio" name="abandon_reason" value={r}
                             checked={reasonChoice === r}
                             onChange={(e) => setReasonChoice(e.target.value)}
                             className="accent-rose-600" />
                      <span className="text-sm text-slate-900">{r}</span>
                    </label>
                  ))}
                </div>
                {reasonChoice === "Other" ? (
                  <input type="text" value={reasonOther}
                         onChange={(e) => setReasonOther(e.target.value)}
                         placeholder="Describe why the plan is being abandoned"
                         data-testid="close-plan-reason-other"
                         disabled={submitting}
                         className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-rose-500" />
                ) : null}
              </div>
            </>
          ) : mode === "ar" ? (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                Plan has an outstanding balance. Closing routes it to AR.
                Pick a reason so the staff can follow up.
              </div>

              <div>
                <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                  AR Reason
                </p>
                <div className="space-y-1.5">
                  {AR_REASONS.map((r) => (
                    <label key={r}
                           data-testid={`close-plan-reason-${r.replace(/\s+/g,'-').toLowerCase()}`}
                           className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${
                             reasonChoice === r
                               ? "border-amber-500 bg-amber-50"
                               : "border-slate-200 bg-white hover:border-slate-400"
                           }`}>
                      <input type="radio" name="ar_reason" value={r}
                             checked={reasonChoice === r}
                             onChange={(e) => setReasonChoice(e.target.value)}
                             className="accent-amber-600" />
                      <span className="text-sm text-slate-900">{r}</span>
                    </label>
                  ))}
                </div>
                {reasonChoice === "Other" ? (
                  <input type="text" value={reasonOther}
                         onChange={(e) => setReasonOther(e.target.value)}
                         placeholder="Describe the AR reason"
                         data-testid="close-plan-reason-other"
                         disabled={submitting}
                         className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-amber-500" />
                ) : null}
              </div>
            </>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-3 text-sm text-emerald-800">
              ✓ Plan paid in full. Closing flips it to Completed and locks the items as audit history.
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-100">
          <button type="button" onClick={() => onOpenChange?.(false)} disabled={submitting}
                  data-testid="close-plan-cancel"
                  className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit}
                  data-testid="close-plan-submit"
                  className={`px-4 py-2 text-sm font-semibold rounded-lg text-white inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${submitTone}`}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" />
              : mode === "abandon" ? <Ban className="w-4 h-4" />
              : <CheckCircle2 className="w-4 h-4" />}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
